import { useEffect, useRef } from 'react';

import { useFeatureFlags } from '@/hooks/featureFlag/useFeatureFlags';
import { useConfig } from '@/hooks/shared/useConfig';
import { useAuthentication } from '@/hooks/user/useAuthentication';

export const useHeapLoader = () => {
  const { isAnalyticsTrackingEnabled } = useFeatureFlags();
  const { config } = useConfig();
  const { user } = useAuthentication();
  const hasLoadedRef = useRef(false);
  const prevEnabledRef = useRef(false);
  const isHeapEnabled = isAnalyticsTrackingEnabled && Boolean(user);

  useEffect(() => {
    const heap = (window as any).heap;
    if (!heap) return;

    const heapId = config.VITE_GS_FE_HEAP_APP_ID;

    if (isHeapEnabled) {
      if (!hasLoadedRef.current && heapId) {
        // First enable on this page load:
        // load the Heap SDK once and remember that this page already initialized it.
        heap.load(heapId);
        hasLoadedRef.current = true;
      } else if (hasLoadedRef.current && !prevEnabledRef.current) {
        // Flag changed from OFF -> ON after Heap had already been loaded before.
        // We cannot rely on heap.startTracking() here because the loaded web SDK
        // does not expose it in your runtime.
        //
        // After heap.stopTracking(), the safe recovery path is a full page reload.
        // On the fresh page load, this hook will run again, see the flag is ON,
        // and call heap.load(heapId) from the first branch above.
        window.location.reload();
        return;
      }
    } else if (prevEnabledRef.current && hasLoadedRef.current) {
      // Flag changed from ON -> OFF after Heap was already loaded in this tab.
      // Stop further tracking for the current page lifetime.
      heap.stopTracking();
    }

    prevEnabledRef.current = isHeapEnabled;
  }, [config.VITE_GS_FE_HEAP_APP_ID, isHeapEnabled]);
};
